import React, { useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Input,
  Table,
  Tag,
  Select,
  Spin,
  List,
  Grid,
  Button,
  Alert,
  message,
  Popover,
  Radio,
  Space,
  theme,
} from "antd";
import {
  SearchOutlined,
  CarOutlined,
  UserOutlined,
  FilterOutlined,
  FileTextOutlined,
  RightOutlined,
  PlusOutlined,
  UndoOutlined,
  SortAscendingOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useWorkOrders } from "@/hooks/useWorkOrders";
import { usePageTitle } from "@/contexts/PageTitleContext";
import { useEffect } from "react";
import { AutoRefreshBadge } from "@/components/AutoRefreshBadge";
import type { WorkOrder } from "@/types";
import { useAuth } from "@/contexts/AuthContext";
import { useWOStatusTypesList } from "@/hooks/useSystemTables";
import axiosInstance from "@/lib/axios";

const { useBreakpoint } = Grid;

export function SchedeLavoro({ detailPath = '/schede-lavoro', showNewButton = false }: { detailPath?: string; showNewButton?: boolean } = {}) {
  const { token } = theme.useToken()
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setTitle } = usePageTitle();
  const { permissions } = useAuth();

  // ── Filtri nella querystring ───────────────────────────────────────────────
  // Stato, ricerca e pagina stanno nell'URL e non in useState: aprendo una
  // scheda questa pagina si smonta, e tornando indietro si rimonta da capo —
  // cio' che stava in useState riparte da zero, l'URL no.
  // `replace: true` di proposito: la voce di cronologia della lista si aggiorna
  // sul posto. Con `push` ogni cambio di filtro ne aggiungerebbe una, e per
  // uscire dalla lista si dovrebbe premere indietro dieci volte.
  //
  // Le scritture nello stesso gesto vanno fuse in una sola. Due chiamate di
  // fila — `setFilterStato(val); setPage(1)` — con `setSearchParams` diretto si
  // annullerebbero a vicenda: la sua versione funzionale legge i parametri del
  // RENDER, non quelli appena scritti, quindi la seconda navigazione
  // cancellerebbe la prima. Qui si accumula su un ref e si scrive una volta.
  // La base e' `window.location.search`, non i parametri del render: l'URL vero
  // e' gia' aggiornato (history.replaceState e' sincrono), quindi una scrittura
  // ritardata — la ricerca, sotto — non riparte da una fotografia vecchia e non
  // cancella cio' che ha scritto qualcun altro nel frattempo (per esempio il
  // cambio di tab in SchedeLavoroBeta).
  const paramsPendenti = useRef<URLSearchParams | null>(null);

  const aggiornaQuery = (patch: Record<string, string | undefined>) => {
    const next = paramsPendenti.current ?? new URLSearchParams(window.location.search);
    for (const [chiave, valore] of Object.entries(patch)) {
      if (valore) next.set(chiave, valore);
      else next.delete(chiave);
    }
    paramsPendenti.current = next;
    queueMicrotask(() => {
      if (!paramsPendenti.current) return;
      const finale = paramsPendenti.current;
      paramsPendenti.current = null;
      setSearchParams(finale, { replace: true });
    });
  };

  const filterStato = searchParams.get('stato') ?? undefined;
  const setFilterStato = (val: string | undefined) => aggiornaQuery({ stato: val });

  const page = Number(searchParams.get('pagina')) || 1;
  const setPage = (p: number) => aggiornaQuery({ pagina: p > 1 ? String(p) : undefined });

  // La ricerca tiene una copia locale, cosi' l'utente scrive senza attese;
  // nell'URL ci finisce dopo mezzo secondo di pausa. Scriverla a ogni tasto
  // premuto riscriverebbe la querystring una volta per lettera.
  const searchDaUrl = searchParams.get('q') ?? '';
  const [searchTerm, setSearchTermLocale] = useState(searchDaUrl);
  const timerRicerca = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => { setSearchTermLocale(searchDaUrl); }, [searchDaUrl]);
  const setSearchTerm = (val: string) => {
    setSearchTermLocale(val);
    if (timerRicerca.current) clearTimeout(timerRicerca.current);
    timerRicerca.current = setTimeout(() => aggiornaQuery({ q: val || undefined }), 500);
  };

  const filterSinistro = searchParams.get('sinistro') === 'true' ? true : undefined;
  const filterScadute = searchParams.get('scadute') === 'true' ? true : undefined;

  const LS_SORT_KEY = 'wo_list_sort';
  const _savedSort = (() => { try { return JSON.parse(localStorage.getItem(LS_SORT_KEY) ?? '{}') } catch { return {} } })();
  const [sortField, setSortField] = useState<string>(_savedSort.field ?? '');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(_savedSort.dir ?? 'desc');

  const [sortPopoverOpen, setSortPopoverOpen] = useState(false);

  const handleSortChange = (field: string, dir: 'asc' | 'desc' | '') => {
    if (field && dir) {
      setSortField(field);
      setSortDir(dir as 'asc' | 'desc');
      setPage(1);
      localStorage.setItem(LS_SORT_KEY, JSON.stringify({ field, dir }));
    } else {
      setSortField('');
      setSortDir('desc');
      setPage(1);
      localStorage.removeItem(LS_SORT_KEY);
    }
  };

  const SORT_FIELDS = [
    { value: 'data_inserimento', label: 'Inserimento' },
    { value: 'data_appuntamento', label: 'Appuntamento' },
    { value: 'numero_scheda', label: 'N. Scheda' },
    { value: 'cliente', label: 'Cliente' },
    { value: 'stato', label: 'Stato' },
  ];

  const sortPopoverContent = (
    <div style={{ width: 220 }}>
      <div style={{ marginBottom: 12, fontWeight: 600, fontSize: 13, color: '#555' }}>Ordina per</div>
      <Radio.Group
        value={sortField || null}
        onChange={(e) => handleSortChange(e.target.value, sortDir)}
        style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}
      >
        {SORT_FIELDS.map(f => (
          <Radio key={f.value} value={f.value}>{f.label}</Radio>
        ))}
      </Radio.Group>
      <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12, display: 'flex', gap: 8 }}>
        <Button
          size="small"
          type={sortDir === 'asc' ? 'primary' : 'default'}
          onClick={() => handleSortChange(sortField || 'data_inserimento', 'asc')}
          style={{ flex: 1 }}
        >↑ Crescente</Button>
        <Button
          size="small"
          type={sortDir === 'desc' ? 'primary' : 'default'}
          onClick={() => handleSortChange(sortField || 'data_inserimento', 'desc')}
          style={{ flex: 1 }}
        >↓ Decrescente</Button>
      </div>
      {sortField && (
        <Button
          size="small"
          type="link"
          danger
          onClick={() => { handleSortChange('', ''); setSortPopoverOpen(false); }}
          style={{ width: '100%', marginTop: 8 }}
        >Rimuovi ordinamento</Button>
      )}
    </div>
  );

  const sortLabel = sortField
    ? `${SORT_FIELDS.find(f => f.value === sortField)?.label ?? sortField} ${sortDir === 'asc' ? '↑' : '↓'}`
    : 'Ordina';
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const { data: statusTypes = [] } = useWOStatusTypesList();

  // Lookup colore/label da DB — indicizzati per nome.toLowerCase() (= valore in record.stato)
  const statoColorMap = Object.fromEntries(statusTypes.map(s => [s.nome.toLowerCase(), s.colore]))
  const statoLabelMap = Object.fromEntries(statusTypes.map(s => [s.nome.toLowerCase(), s.nome]))

  const handleNuovaScheda = () => {
    navigate(`${detailPath}/new`)
  }

  // Stati visibili basati su dashboard.stati.*; se nessuno → tutti visibili
  // Il suffisso del permesso è il codice dello stato (stabile, lowercase con underscore)
  // Il value inviato all'API è nome.toLowerCase() (= valore memorizzato in work_orders.stato)
  const allowedStati = permissions
    .filter(p => p.startsWith('dashboard.stati.'))
    .map(p => p.replace('dashboard.stati.', ''))
  const visibleStatoOptions = statusTypes
    .filter(s => allowedStati.length === 0 || allowedStati.includes(s.codice ?? s.nome))
    .map(s => ({ value: s.nome.toLowerCase(), label: s.nome }))

  useEffect(() => {
    setTitle("Schede Lavoro");
  }, [setTitle]);

  const { data, isLoading, refetch, dataUpdatedAt } = useWorkOrders(page, 50, searchTerm || undefined, filterStato, filterSinistro, filterScadute, sortField || undefined, sortDir);
  const workOrders = data?.items ?? [];
  const total = data?.total ?? 0;

  const handleRipristina = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation()
    try {
      const res = await axiosInstance.post(`/work-orders/${id}/restore`)
      message.success(`Scheda ripristinata (stato: ${res.data.restored_stato})`)
      refetch()
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Errore durante il ripristino')
    }
  }

  const columns: ColumnsType<WorkOrder> = [
    {
      title: "Numero",
      dataIndex: "numero_scheda",
      key: "numero_scheda",
      width: 140,
      render: (numero: string) => (
        <span style={{ fontWeight: 600, color: "#1890ff" }}>
          <FileTextOutlined style={{ marginRight: 6 }} />
          {numero}
        </span>
      ),
    },
    {
      title: "Cliente",
      dataIndex: "customer_nome",
      key: "cliente",
      width: 160,
      render: (nome: string) => (
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <UserOutlined style={{ color: "#8c8c8c" }} />
          {nome ?? "—"}
        </span>
      ),
    },
    {
      title: "Veicolo",
      key: "veicolo",
      width: 180,
      render: (_, record) => (
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <CarOutlined style={{ color: "#8c8c8c" }} />
          {[record.vehicle_marca, record.vehicle_modello].filter(Boolean).join(" ") || "—"}
        </span>
      ),
    },
    {
      title: "Targa",
      dataIndex: "vehicle_targa",
      key: "vehicle_targa",
      width: 100,
      render: (targa: string) =>
        targa ? (
          <Tag color="geekblue" style={{ fontWeight: 600 }}>
            {targa}
          </Tag>
        ) : (
          "—"
        ),
    },
    {
      title: "Appuntamento",
      dataIndex: "data_appuntamento",
      key: "data_appuntamento",
      width: 130,
      render: (data: string) =>
        data ? new Date(data).toLocaleDateString("it-IT") : "—",
    },
    {
      title: "Entrata Veicolo",
      dataIndex: "data_entrata_veicolo",
      key: "data_entrata_veicolo",
      width: 130,
      render: (data: string) =>
        data ? new Date(data).toLocaleDateString("it-IT") : "—",
    },
    {
      title: "Stato",
      dataIndex: "stato",
      key: "stato",
      width: 130,
      render: (stato: string) => (
        <Tag color={statoColorMap[stato] ?? "default"}>
          {statoLabelMap[stato] ?? stato}
        </Tag>
      ),
    },
    {
      title: "Off. est.",
      key: "has_external_workshop",
      width: 75,
      render: (_: any, record: WorkOrder) =>
        (record as any).has_external_workshop ? (
          <Tag color="orange" style={{ fontSize: 11 }}>sì</Tag>
        ) : null,
    },
    ...(filterStato === 'annullata' ? [{
      title: '',
      key: 'ripristina',
      width: 120,
      render: (_: any, r: WorkOrder) => (
        <Button
          size="small"
          icon={<UndoOutlined />}
          onClick={(e) => handleRipristina(e, r.id)}
        >
          Ripristina
        </Button>
      ),
    }] : []),
  ];

  return (
    <div style={{ padding: "0 0 16px" }}>
      {/* Toolbar: stacked on mobile, inline on desktop */}
      {isMobile ? (
        <div style={{ marginBottom: 12 }}>
          {showNewButton && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              block
              style={{ marginBottom: 8 }}
              onClick={handleNuovaScheda}
            >
              Nuova Scheda
            </Button>
          )}
          <Input
            placeholder="Cerca numero, cliente, targa..."
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
            allowClear
            style={{ marginBottom: 8 }}
          />
          <Select
            value={filterStato}
            onChange={(val) => { setFilterStato(val); setPage(1); }}
            style={{ width: '100%' }}
            placeholder="Tutti gli stati"
            allowClear
            suffixIcon={<FilterOutlined />}
            options={visibleStatoOptions}
          />
          <Popover
            content={sortPopoverContent}
            trigger="click"
            open={sortPopoverOpen}
            onOpenChange={setSortPopoverOpen}
            placement="bottomLeft"
          >
            <Button
              icon={<SortAscendingOutlined />}
              type={sortField ? 'primary' : 'default'}
              block
              style={{ marginTop: 8 }}
            >
              {sortLabel}
            </Button>
          </Popover>
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
            <p style={{ margin: 0, fontSize: 13, color: "#8c8c8c" }}>{total} schede totali</p>
            <AutoRefreshBadge updatedAt={dataUpdatedAt || null} />
          </div>
        </div>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <p style={{ margin: 0, fontSize: 14, color: "#8c8c8c" }}>{total} schede totali</p>
              <AutoRefreshBadge updatedAt={dataUpdatedAt || null} />
            </div>
            {showNewButton && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleNuovaScheda}
              >
                Nuova Scheda
              </Button>
            )}
          </div>
          <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
            <Input
              placeholder="Cerca per numero, cliente, veicolo, targa..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
              style={{ flex: 1 }}
              allowClear
            />
            <Select
              value={filterStato}
              onChange={(val) => { setFilterStato(val); setPage(1); }}
              style={{ width: 180 }}
              placeholder="Tutti gli stati"
              allowClear
              suffixIcon={<FilterOutlined />}
              options={visibleStatoOptions}
            />
            <Popover
              content={sortPopoverContent}
              trigger="click"
              open={sortPopoverOpen}
              onOpenChange={setSortPopoverOpen}
              placement="bottomRight"
            >
              <Button
                icon={<SortAscendingOutlined />}
                type={sortField ? 'primary' : 'default'}
              >
                {sortLabel}
              </Button>
            </Popover>
          </div>
        </>
      )}

      {filterStato === 'annullata' && (
        <Alert
          type="warning"
          showIcon
          message="Stai visualizzando le schede annullate. Clicca 'Ripristina' per riportare una scheda al suo stato precedente."
          style={{ marginBottom: 12 }}
        />
      )}
      {filterStato === 'riconsegnata' && (
        <Alert
          type="info"
          showIcon
          message="Stai visualizzando le schede riconsegnate. Di default non sono visibili nella lista."
          style={{ marginBottom: 12 }}
        />
      )}
      {filterSinistro && (
        <Alert
          type="info"
          showIcon
          message="Filtro attivo: schede con sinistro aperto."
          style={{ marginBottom: 12 }}
        />
      )}
      {filterScadute && (
        <Alert
          type="error"
          showIcon
          message="Filtro attivo: schede in ritardo (data fine prevista superata)."
          style={{ marginBottom: 12 }}
        />
      )}

      <Spin spinning={isLoading}>
        {isMobile ? (
          <List
            dataSource={workOrders}
            rowKey="id"
            pagination={{
              current: page,
              pageSize: 50,
              total,
              onChange: setPage,
              showSizeChanger: false,
              simple: true,
            }}
            renderItem={(record) => (
              <div
                onClick={() => navigate(`${detailPath}/${record.id}`)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  background: record.stato === 'bozza' ? '#faf5ff' : token.colorBgContainer,
                  borderRadius: 8,
                  marginBottom: 8,
                  padding: record.stato === 'bozza' ? '12px 14px 12px 10px' : '12px 14px',
                  borderLeft: record.stato === 'bozza' ? '4px solid #7c3aed' : 'none',
                  boxShadow: record.stato === 'bozza' ? '0 2px 8px rgba(124,58,237,0.15)' : '0 1px 4px rgba(0,0,0,0.08)',
                  cursor: 'pointer',
                  gap: 12,
                }}
              >
                {/* Left: numero + stato */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontWeight: 700, fontSize: 15, color: '#1890ff' }}>
                      <FileTextOutlined style={{ marginRight: 4 }} />
                      {record.numero_scheda}
                    </span>
                    {record.priorita === 'urgente' && <Tag color="red" style={{ margin: 0, fontSize: 11 }}>Urgente</Tag>}
                    {record.priorita === 'alta' && <Tag color="orange" style={{ margin: 0, fontSize: 11 }}>Alta</Tag>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#444', fontSize: 13, marginBottom: 4, overflow: 'hidden' }}>
                    <UserOutlined style={{ color: '#8c8c8c', flexShrink: 0 }} />
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {record.customer_nome ?? '—'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#666' }}>
                    <CarOutlined style={{ color: '#8c8c8c', flexShrink: 0 }} />
                    <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {[record.vehicle_marca, record.vehicle_modello].filter(Boolean).join(' ') || '—'}
                    </span>
                    {record.vehicle_targa && (
                      <Tag color="geekblue" style={{ margin: 0, fontWeight: 600, fontSize: 12, flexShrink: 0 }}>
                        {record.vehicle_targa}
                      </Tag>
                    )}
                  </div>
                  <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Tag color={statoColorMap[record.stato] ?? 'default'} style={{ margin: 0, fontSize: 11 }}>
                      {statoLabelMap[record.stato] ?? record.stato}
                    </Tag>
                    {record.data_appuntamento && (
                      <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                        {new Date(record.data_appuntamento).toLocaleDateString('it-IT')}
                      </span>
                    )}
                  </div>
                </div>
                <RightOutlined style={{ color: '#ccc', flexShrink: 0 }} />
              </div>
            )}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={workOrders}
            rowKey="id"
            size="small"
            pagination={{
              current: page,
              pageSize: 50,
              total,
              onChange: setPage,
              showSizeChanger: false,
              showTotal: (t) => `Totale ${t} schede`,
            }}
            scroll={{ x: 900 }}
            onRow={(record) => ({
              onClick: () => navigate(`${detailPath}/${record.id}`),
              style: { cursor: "pointer" },
            })}
            rowClassName={(record: any) => record.stato === 'bozza' ? 'scheda-row wo-row-bozza' : 'scheda-row'}
          />
        )}
      </Spin>
    </div>
  );
}

export default SchedeLavoro;
